import { useMemo, useState, type FormEvent } from "react";

import { Check, Loader2, X } from "lucide-react";

import { GradientScene, SceneError, SceneStep } from "@/components/auth/GradientScene";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/lib/api-client";

/**
 * The first screen a new account ever sees.
 *
 * An invited user is created with `passwordChangedAt` at the epoch, which the server reads
 * as expired — so the emailed temporary password buys exactly one trip through here and is
 * spent. The same screen serves a genuinely expired password.
 */

/** ⚠ Mirrors `validatePasswordPolicy` on the server. The server is authoritative; this only
 *  spares people a round trip. If one changes, change both. */
const RULES: { label: string; test: (v: string) => boolean }[] = [
  { label: "At least 12 characters", test: (v) => v.length >= 12 },
  { label: "One uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { label: "One lowercase letter", test: (v) => /[a-z]/.test(v) },
  { label: "One number", test: (v) => /\d/.test(v) },
];

export function ForceChangePassword() {
  const { forceChangePassword } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [details, setDetails] = useState<string[]>();
  const [busy, setBusy] = useState(false);

  const results = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(password) })), [password]);
  const allMet = results.every((r) => r.ok);
  const matches = password.length > 0 && password === confirm;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setDetails(undefined);
    if (!matches) {
      setError("The two passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await forceChangePassword(password);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setDetails(err.details);
      } else {
        setError("Could not reach the server. Check your connection and try again.");
      }
      setBusy(false);
    }
  }

  return (
    <GradientScene>
      <div>
        <SceneStep label="Step 1 of 2 — Choose a password" />
        <h2 className="mt-2 text-xl font-bold text-white">Set your password</h2>
        <p className="mt-1 text-sm text-white/60">
          Your current password was temporary. Choose one only you know — you will set up an
          authenticator app next.
        </p>
      </div>

      <form className="space-y-4" onSubmit={onSubmit}>
        <SceneError message={error} details={details} />

        <div>
          <label htmlFor="new-password" className="mb-1.5 block text-sm font-medium text-white/80">
            New password
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="glass-input w-full rounded-lg px-4 py-2.5 text-sm"
          />
        </div>

        <ul className="space-y-1">
          {results.map((r) => (
            <li
              key={r.label}
              className={`flex items-center gap-2 text-xs ${r.ok ? "text-white/80" : "text-white/45"}`}
            >
              {r.ok ? (
                <Check className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <X className="h-3.5 w-3.5 shrink-0" />
              )}
              {r.label}
            </li>
          ))}
        </ul>

        <div>
          <label htmlFor="confirm-password" className="mb-1.5 block text-sm font-medium text-white/80">
            Confirm password
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="glass-input w-full rounded-lg px-4 py-2.5 text-sm"
          />
          {confirm.length > 0 && !matches && (
            <p className="mt-1.5 text-xs text-red-200">The two passwords do not match.</p>
          )}
        </div>

        <button
          type="submit"
          disabled={busy || !allMet || !matches}
          className="gradient-btn flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? "Saving…" : "Continue"}
        </button>
      </form>
    </GradientScene>
  );
}
